import {
  Badge,
  Box,
  Button,
  Center,
  DrawerBody,
  DrawerHeader,
  Flex,
  HStack,
  IconButton,
  Input,
  Spinner,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { FormEvent, useState } from 'react';
import { FiCheck, FiEdit2, FiPlusCircle, FiTrash2, FiX } from 'react-icons/fi';

import { BiView } from '../../../../services/biService';
import { DrawerComponent } from '../index';

interface PropsDrawer {
  isOpen: boolean;
  onClose: () => void;
  views: BiView[];
  isLoading: boolean;
  currentUserId: string;
  onApply: (view: BiView) => void;
  onSaveCurrent: () => void;
  onRename: (view: BiView, name: string) => Promise<void>;
  onDelete: (view: BiView) => Promise<void>;
}

function ViewItem({
  view,
  isOwner,
  onApply,
  onRename,
  onDelete,
}: {
  view: BiView;
  isOwner: boolean;
  onApply: (target: BiView) => void;
  onRename: (target: BiView, name: string) => Promise<void>;
  onDelete: (target: BiView) => Promise<void>;
}) {
  const [mode, setMode] = useState<'view' | 'rename' | 'delete'>('view');
  const [name, setName] = useState(view.name);
  const [busy, setBusy] = useState(false);

  const handleRename = async (event: FormEvent) => {
    event.preventDefault();

    if (!name.trim() || name.trim() === view.name) {
      setMode('view');
      return;
    }

    setBusy(true);
    try {
      await onRename(view, name.trim());
      setMode('view');
    } catch {
      // Quem chamou já mostrou o aviso de erro.
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await onDelete(view);
    } catch {
      setMode('view');
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'rename') {
    return (
      <Flex as="form" onSubmit={handleRename} gridGap="2" align="center">
        <Input
          size="sm"
          value={name}
          onChange={event => setName(event.target.value)}
          aria-label="Novo nome da visualização"
          autoFocus
        />
        <IconButton
          aria-label="Salvar nome"
          icon={<FiCheck />}
          size="sm"
          colorScheme="blue"
          type="submit"
          isLoading={busy}
        />
        <IconButton
          aria-label="Cancelar"
          icon={<FiX />}
          size="sm"
          variant="ghost"
          onClick={() => {
            setName(view.name);
            setMode('view');
          }}
        />
      </Flex>
    );
  }

  return (
    <Flex
      align="center"
      gridGap="2"
      borderWidth="1px"
      borderColor="gray.700"
      borderRadius="md"
      px="3"
      py="2"
    >
      <Box
        as="button"
        type="button"
        flex="1"
        minW="0"
        textAlign="left"
        onClick={() => onApply(view)}
        _hover={{ color: 'blue.500' }}
      >
        <Text fontWeight="medium" noOfLines={1}>
          {view.name}
        </Text>
        {view.public && (
          <Badge colorScheme="blue" fontSize="2xs">
            Pública
          </Badge>
        )}
      </Box>

      {isOwner && mode === 'view' && (
        <HStack spacing="1">
          <Tooltip label="Renomear" hasArrow>
            <IconButton
              aria-label="Renomear visualização"
              icon={<FiEdit2 />}
              size="sm"
              variant="ghost"
              onClick={() => setMode('rename')}
            />
          </Tooltip>
          <Tooltip label="Excluir" hasArrow>
            <IconButton
              aria-label="Excluir visualização"
              icon={<FiTrash2 />}
              size="sm"
              variant="ghost"
              colorScheme="red"
              onClick={() => setMode('delete')}
            />
          </Tooltip>
        </HStack>
      )}

      {isOwner && mode === 'delete' && (
        <HStack spacing="1">
          <Text fontSize="xs" color="gray.400">
            Excluir?
          </Text>
          <Button
            size="xs"
            colorScheme="red"
            isLoading={busy}
            onClick={handleDelete}
          >
            Sim
          </Button>
          <Button size="xs" variant="ghost" onClick={() => setMode('view')}>
            Não
          </Button>
        </HStack>
      )}
    </Flex>
  );
}

export function DrawerNavigationBi({
  isOpen,
  onClose,
  views,
  isLoading,
  currentUserId,
  onApply,
  onSaveCurrent,
  onRename,
  onDelete,
}: PropsDrawer) {
  const mine = views.filter(view => view.access_id === currentUserId);
  const shared = views.filter(view => view.access_id !== currentUserId);

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      placement="right"
      size="sm"
    >
      <DrawerHeader borderBottomWidth="1px">Minhas visualizações</DrawerHeader>

      <DrawerBody py="5">
        <Button
          leftIcon={<FiPlusCircle />}
          colorScheme="blue"
          w="100%"
          mb="2"
          onClick={onSaveCurrent}
        >
          Salvar visualização atual
        </Button>
        <Text fontSize="xs" color="gray.500" mb="6">
          Guarda os filtros e a página que estão na tela agora.
        </Text>

        {isLoading ? (
          <Center py="6">
            <Spinner color="blue.500" />
          </Center>
        ) : (
          <Stack spacing="6">
            <Box>
              <Text fontWeight="bold" color="gray.400" fontSize="small" mb="3">
                SALVAS POR VOCÊ
              </Text>
              {mine.length === 0 ? (
                <Text fontSize="sm" color="gray.500">
                  Você ainda não salvou visualizações neste relatório.
                </Text>
              ) : (
                <Stack spacing="2">
                  {mine.map(view => (
                    <ViewItem
                      key={view.id}
                      view={view}
                      isOwner
                      onApply={onApply}
                      onRename={onRename}
                      onDelete={onDelete}
                    />
                  ))}
                </Stack>
              )}
            </Box>

            {shared.length > 0 && (
              <Box>
                <Text
                  fontWeight="bold"
                  color="gray.400"
                  fontSize="small"
                  mb="3"
                >
                  PÚBLICAS
                </Text>
                <Stack spacing="2">
                  {shared.map(view => (
                    <ViewItem
                      key={view.id}
                      view={view}
                      isOwner={false}
                      onApply={onApply}
                      onRename={onRename}
                      onDelete={onDelete}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DrawerBody>
    </DrawerComponent>
  );
}
